import { deepEquals } from '@jack-henry/frontend-utils/functions';
import { LitElement, PropertyValues } from 'lit';

type PropChangeHandler<E extends LitElement, PropName extends keyof E> = (
    current: E[PropName],
    previous: E[PropName]
) => void;

/**
 * A utility class for managing property changes on LitElement instances.
 */
export class PropertyChangeManager<E extends LitElement> {
    constructor(private element: E) {}

    private readonly handlers = new Map<keyof E, PropChangeHandler<E, keyof E>>();

    public onPropertyChange<PropName extends keyof E>(
        prop: PropName,
        handler: PropChangeHandler<E, PropName>
    ) {
        this.handlers.set(prop, handler as PropChangeHandler<E, keyof E>);
        return this;
    }

    /**
     * Invoke this method during the `firstUpdated()` or `updated()` lifecycle method
     * of the LitElement instance, passing the property change map.
     *
     * @param ignoreUnchanged If true, handlers will not be called if the
     * previous and current values are equal.
     */
    public handleChanges(changedProps: PropertyValues, ignoreUnchanged = true) {
        changedProps.forEach((_, prop) => {
            const handle = this.handlers.get(prop as keyof E);
            if (!handle) {
                return;
            }

            const previous = changedProps.get(prop as keyof E) as E[keyof E];
            const current = this.element[prop as keyof E];

            if (ignoreUnchanged && deepEquals(previous, current)) {
                return;
            }

            handle(current, previous);
        });
    }
}
